import { ErrorResponse, Member, SuccessResponse, User } from '@citruscamps/citrus-client'
import { useState } from 'react'
import translations from '../translations/en.json'
import { HttpClient } from '../utils/http'
import { useAuth } from './useAuth'
import { useRequestHandler } from './useRequestHandler'

export interface ISupportRequest {
  type: 'billing' | 'bug' | 'suggest_feature' | 'tech_support'
  summary: string
  description: string
  error_stack?: string
}

export interface SupportValue {
  user?: User
  member?: Member
  hasSubmitted: boolean
  isError: boolean
  isSuccess: boolean
  error: ErrorResponse | null
  handleSubmit: (values: ISupportRequest) => Promise<any>
}

export const useSupport = (): SupportValue => {
  const { requestHandler } = useRequestHandler()
  const { user, member, program } = useAuth()
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)
  const [error, setError] = useState<ErrorResponse | null>(null)

  const handleSubmit = async (values: ISupportRequest): Promise<void> => {
    if (values) {
      try {
        setError(null)
        const client = new HttpClient()
        const headers: any = {}
        await requestHandler<SuccessResponse>(() =>
          client.post(
            '/api/v1/support',
            {
              reporter_name: [user?.first_name, user?.last_name].join(' '),
              reporter_email: user?.email,
              type: values.type,
              summary: values.summary,
              description: values.description,
              member_id: member?.id,
              program_id: program?.id,
              program_name: program?.name,
              url: window.location.href,
              app_version: process.env.REACT_APP_VERSION,
              error_stack: values.error_stack,
            },
            {
              headers,
            },
          ),
        )
        setHasSubmitted(true)
      } catch (e: any) {
        setError({
          type: e?.type || 'unknown',
          code: e?.code || 500,
          message: e.message || translations['error.default_toast_header'],
          ...(e?.param ? { param: e.param } : {}),
          ...(e?.path ? { path: e.path } : {}),
          timestamp: e.timestamp || new Date().toJSON(),
        })
        setHasSubmitted(true)
        throw e
      }
    }
  }

  return {
    hasSubmitted,
    isError: error !== null,
    isSuccess: error === null,
    error,
    user,
    member,
    handleSubmit,
  }
}
